import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw, Calendar, TrendingUp, Filter, MoreVertical } from 'lucide-react';
import DatePickerButton from '../ui/date-picker-button';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import { KpiNames } from '../../constants/kpi-constants';
import moment from 'moment';
import { useToast } from '../../hooks/use-toast';
import { todayISO } from '../../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const HistoricalTechPar = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [kpiData, setKpiData] = useState([]);
  const [filteredKpiData, setFilteredKpiData] = useState([]);
  const [selectedKPIs, setSelectedKPIs] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('');
  
  // Performance benchmark chart states
  const [selectedKPIBar, setSelectedKPIBar] = useState('performance_ratio');
  const [startDateBar, setStartDateBar] = useState('');
  const [endDateBar, setEndDateBar] = useState('');
  const [selectedDateRangeBar, setSelectedDateRangeBar] = useState('');
  const [chartData, setChartData] = useState([]);
  const [selectedKPIName, setSelectedKPIName] = useState('Performance Ratio (%)');
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBarMenuOpen, setIsBarMenuOpen] = useState(false);
  const { toast } = useToast();

  // Specific KPI names for filtering
  const specificKPINames = [
    'Plant Availability (%)', 'Grid Availability (%)', 'Tilt POA Irradiation(kWh/m^2)', 
    'Net Energy (kWh)', 'Energy Export (kWh)', 'Performance Ratio (%)', 
    'Temp Corr PR (%)', 'Specific Yield (kWh/kWp)', 'Expected Energy(kWh)', 
    'DC CUF (%)', 'AC CUF (%)'
  ];

  // Filtered KPI list based on specific names
  const kpiList = Object.keys(KpiNames)
    .filter((key) => specificKPINames.includes(KpiNames[key].name))
    .map((key) => ({ key, name: KpiNames[key].name }));

  useEffect(() => {
    // Initialize dates
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    setStartDate(yesterday.toISOString().split('T')[0]);
    setEndDate(yesterday.toISOString().split('T')[0]);
    
    // Initialize bar chart dates (last 15 days)
    const defaultEndDate = new Date(yesterday);
    const defaultStartDate = new Date(defaultEndDate);
    defaultStartDate.setDate(defaultStartDate.getDate() - 15);
    
    setStartDateBar(defaultStartDate.toISOString().split('T')[0]);
    setEndDateBar(defaultEndDate.toISOString().split('T')[0]);
    
    // Set initial selected KPIs
    setSelectedKPIs(Object.keys(KpiNames));
    
    // Set initial KPI for bar chart
    setSelectedKPIBar('performance_ratio');
    setSelectedKPIName('Performance Ratio (%)');
    
    // initial data will be loaded by the dedicated effects once dates are set
    
    // Set up auto-refresh (every minute)
    const refreshInterval = setInterval(() => {
      loadKpiData();
    }, 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Auto load KPI card data whenever the selected date range changes
  useEffect(() => {
    if (startDate && endDate) {
      loadKpiData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Auto load benchmark chart whenever its range or KPI changes
  useEffect(() => {
    if (startDateBar && endDateBar) {
      loadChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateBar, endDateBar, selectedKPIBar]);

  useEffect(() => {
    // Filter KPI data when selection changes
    if (kpiData.length > 0) {
      setFilteredKpiData(kpiData.filter(data => selectedKPIs.includes(data.kpiCode)));
    }
  }, [selectedKPIs, kpiData]);

  const loadKpiData = async () => {
    if (!startDate || !endDate) return;
    
    try {
      setIsLoading(true);
      const requestBody = {
        start_date: `${startDate} 00:00:00`,
        end_date: `${endDate} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      };

      // Try aggregated KPI endpoint first
      try {
        const response = await apiService.post(API_CONFIG.ENDPOINTS.GET_AGGREGATED_KPI, requestBody);
        if (response.data && response.data.length > 0) {
          const kpiReadings = response.data[0].readings[0].KPI;
          processKpiData(kpiReadings);
        } else {
          // Fallback to graph endpoint
          await loadKpiDataFromGraph(requestBody);
        }
      } catch (error) {
        // Fallback to graph endpoint
        await loadKpiDataFromGraph(requestBody);
      }
    } catch (error) {
      console.error('Error loading KPI data:', error);
      toast({
        title: "Error",
        description: "Failed to load KPI data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadKpiDataFromGraph = async (requestBody) => {
    try {
      const response = await apiService.post(API_CONFIG.ENDPOINTS.GET_FOR_GRAPH, requestBody);
      if (response.data && response.data.length > 0) {
        const kpiReadings = response.data[response.data.length - 1].readings[0].KPI;
        processKpiData(kpiReadings);
      }
    } catch (error) {
      console.error('Error loading KPI data from graph endpoint:', error);
    }
  };

  const processKpiData = (kpiReadings) => {
    const results = [];
    
    for (let [key, value] of Object.entries(kpiReadings)) {
      if (key !== 'specific_power' && KpiNames[key] && specificKPINames.includes(KpiNames[key].name)) {
        results.push({
          kpiName: KpiNames[key].name,
          kpiValue: value,
          kpiCode: key,
          image: getImageUrl(key)
        });
      }
    }
    
    setKpiData(results);
    setFilteredKpiData(results);
  };

  const loadChartData = async () => {
    if (!startDateBar || !endDateBar) return;
    
    try {
      const requestBody = {
        start_date: `${startDateBar} 00:00:00`,
        end_date: `${endDateBar} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      };

      const response = await apiService.post(API_CONFIG.ENDPOINTS.GET_FOR_GRAPH, requestBody);
      
      if (response.data && response.data.length > 0) {
        const chartDataArray = [];
        
        response.data.forEach((data) => {
          const kpiList = data.readings[0].KPI;
          const performanceRatioKPI = kpiList.find((kpi) => kpi[selectedKPIBar] !== undefined);
          
          if (performanceRatioKPI) {
            chartDataArray.push({
              date: moment(data.timestamp).format('DD MMM YYYY'),
              observed: performanceRatioKPI[selectedKPIBar],
              timestamp: data.timestamp
            });
          }
        });
        
        setChartData(chartDataArray);
      }
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };

  const getImageUrl = (kpiName) => {
    const imageData = {
      plant_availability: '/energy-import.svg',
      grid_availability: '/energy-export.svg',
      net_energy: '/energy-import.svg',
      total_energy_loss: '/power-grid.svg',
      specific_yield: '/energy-import.svg',
      specific_power: '/energy-import.svg',
      performance_ratio: '/energy-import.svg',
      temp_corr_ratio: '/temperature-correction.svg',
      capacity_uti_factor: '/capacity-factor.svg',
      expected_energy: '/power-grid.svg',
      ac_cuf_daily: '/power-grid.svg',
      dc_cuf_daily: '/power-grid.svg'
    };
    
    return imageData[kpiName] || '/powerenergy.svg';
  };

  const handleKPIChange = (event) => {
    setSelectedKPIs(event);
  };

  const handleBarKPIChange = (kpi) => {
    setSelectedKPIBar(kpi.key);
    setSelectedKPIName(kpi.name);
    loadChartData();
  };

  const applyFilter = () => {
    if (startDate && endDate) {
      const start = moment(startDate).format('DD MMM YYYY');
      const end = moment(endDate).format('DD MMM YYYY');
      setSelectedDateRange(`${start} to ${end}`);
      loadKpiData();
      setIsMenuOpen(false);
    }
  };

  const applyBarFilter = () => {
    if (startDateBar && endDateBar) {
      const start = moment(startDateBar).format('DD MMM YYYY');
      const end = moment(endDateBar).format('DD MMM YYYY');
      setSelectedDateRangeBar(`${start} to ${end}`);
      loadChartData();
      setIsBarMenuOpen(false);
    }
  };

  const refreshData = () => {
    loadKpiData();
    loadChartData();
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <h1 className="text-2xl sm:text-3xl font-bold">Historical Technical Parameters</h1>
        <Button onClick={refreshData} variant="outline" size="sm" className="w-full sm:w-auto">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Performance Indicators Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Key Performance Indicators</CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">{selectedDateRange}</span>
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
              
              {isMenuOpen && (
                <div className="absolute right-0 top-10 w-72 sm:w-80 bg-white dark:bg-gray-800 border rounded-lg shadow-lg z-50 p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">KPI to show</label>
                      <select
                        multiple
                        value={selectedKPIs}
                        onChange={(e) => {
                          const values = Array.from(e.target.selectedOptions, option => option.value);
                          setSelectedKPIs(values);
                        }}
                        className="w-full mt-1 border rounded-md p-2"
                      >
                        {kpiList.map((kpi) => (
                          <option key={kpi.key} value={kpi.key}>
                            {kpi.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Start Date</label>
                        <DatePickerButton
                          selectedDate={startDate}
                          onDateChange={setStartDate}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">End Date</label>
                        <DatePickerButton
                          selectedDate={endDate}
                          onDateChange={setEndDate}
                        />
                      </div>
                    </div>
                    
                    <Button onClick={applyFilter} className="w-full">
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {filteredKpiData.map((kpi, index) => (
                <div key={index} className="border rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <img src={kpi.image} alt="" className="w-6 h-6" />
                    <span className="text-lg font-semibold">
                      {typeof kpi.kpiValue === 'number' ? kpi.kpiValue.toFixed(2) : kpi.kpiValue}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">{kpi.kpiName}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Benchmark Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Plant Performance Summary</CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">{selectedDateRangeBar}</span>
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsBarMenuOpen(!isBarMenuOpen)}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
              
              {isBarMenuOpen && (
                <div className="absolute right-0 top-10 w-72 sm:w-80 bg-white dark:bg-gray-800 border rounded-lg shadow-lg z-50 p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">KPI to show</label>
                      <select
                        value={selectedKPIBar}
                        onChange={(e) => {
                          const kpi = kpiList.find(k => k.key === e.target.value);
                          if (kpi) handleBarKPIChange(kpi);
                        }}
                        className="w-full mt-1 border rounded-md p-2"
                      >
                        {kpiList.map((kpi) => (
                          <option key={kpi.key} value={kpi.key}>
                            {kpi.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Start Date</label>
                        <DatePickerButton
                          selectedDate={startDateBar}
                          onDateChange={setStartDateBar}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">End Date</label>
                        <DatePickerButton
                          selectedDate={endDateBar}
                          onDateChange={setEndDateBar}
                        />
                      </div>
                    </div>
                    
                    <Button onClick={applyBarFilter} className="w-full">
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64 sm:h-80 md:h-96">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, selectedKPIName]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Legend />
                  <Bar 
                    dataKey="observed" 
                    fill="rgb(23, 107, 239)" 
                    name={`${selectedKPIName} (%)`}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No chart data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoricalTechPar;
